'use client'

import React, { useCallback, useEffect, useState } from 'react'
import { cn } from '@scalis/utils'
import type { ExportRecord } from '~/utils/csv-export/types'
import { ButtonIcon } from '../button-icon'
import { ButtonIconKind } from '../button-icon/button-icon.types'

interface CSVExportProgressToastProps {
  /** Export record containing all export state */
  export: ExportRecord
  /** Whether the toast is minimized */
  isMinimized?: boolean
  /** Callback when user clicks minimize */
  onMinimize?: () => void
  /** Callback when user clicks expand */
  onExpand?: () => void
  /** Callback when user cancels the export */
  onCancel?: (exportId: string) => void
  /** Callback when user dismisses/closes the toast */
  onDismiss?: (exportId: string) => void
  /** Callback when user downloads completed export */
  onDownload?: (exportId: string) => void
  /** Additional CSS classes */
  className?: string
}

export const CSVExportProgressToast: React.FC<CSVExportProgressToastProps> = ({
  export: exportRecord,
  isMinimized = false,
  onMinimize,
  onExpand,
  onCancel,
  onDismiss,
  onDownload,
  className,
}) => {
  const [isAnimating, setIsAnimating] = useState(false)

  const { status, config, error } = exportRecord
  const { state, currentRecords, totalRecords, percentage } = status

  // Calculate overall progress percentage
  const progressPercentage = Math.round(percentage)

  // Determine toast state and styling
  const isCompleted = state === 'completed'
  const isError = state === 'error' || state === 'cancelled'
  const isActive = ['queued', 'initializing', 'fetching', 'processing'].includes(state)

  // Handle minimize action (always minimizes)
  const handleMinimize = useCallback(() => {
    setIsAnimating(true)
    onMinimize?.()

    // Reset animation state
    setTimeout(() => setIsAnimating(false), 300)
  }, [onMinimize])

  // Handle expand action (always expands)
  const handleExpand = useCallback(() => {
    setIsAnimating(true)
    onExpand?.()

    // Reset animation state
    setTimeout(() => setIsAnimating(false), 300)
  }, [onExpand])

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel(exportRecord.id)
    }
  }, [exportRecord.id, onCancel])

  const handleDismiss = useCallback(() => {
    if (onDismiss) {
      onDismiss(exportRecord.id)
    }
  }, [exportRecord.id, onDismiss])

  // Choose the appropriate close handler based on export state
  const handleClose = useCallback(() => {
    if (isActive && onCancel) {
      // For active exports, cancel the export
      handleCancel()
    } else if (onDismiss) {
      // For completed/error/cancelled exports, just dismiss the toast
      handleDismiss()
    }
  }, [isActive, onCancel, onDismiss, handleCancel, handleDismiss])

  const handleDownload = useCallback(() => {
    if (onDownload) {
      onDownload(exportRecord.id)
    }
  }, [exportRecord.id, onDownload])

  // Auto-expand on completion (unless already minimized by user)
  useEffect(() => {
    if (isCompleted && isMinimized) {
      onExpand?.()
    }
  }, [isCompleted, isMinimized, onExpand])

  // Get status-specific styling
  const getStatusStyling = () => {
    if (isError) {
      return {
        containerClass: 'border-status-critical-10 bg-status-critical-light backdrop-blur-sm',
        iconClass: 'fa-solid fa-circle-exclamation text-status-critical',
        progressBarClass: 'bg-status-critical',
      }
    }

    if (isCompleted) {
      return {
        containerClass: 'border-status-success-10 bg-status-success-light backdrop-blur-sm',
        iconClass: 'fa-solid fa-circle-check text-status-success',
        progressBarClass: 'bg-status-success',
      }
    }

    return {
      containerClass: 'border-brand-primary-rest bg-white backdrop-blur-sm',
      iconClass: 'fa-solid fa-download text-brand-primary-rest',
      progressBarClass: 'bg-brand-primary-rest',
    }
  }

  const { containerClass, iconClass, progressBarClass } = getStatusStyling()

  // Get status message
  const getStatusMessage = () => {
    switch (state) {
      case 'queued':
        return 'Export queued...'
      case 'initializing':
        return 'Initializing export...'
      case 'fetching':
        return `Fetching data (${currentRecords || 0}/${totalRecords || 0} records)...`
      case 'processing':
        return 'Generating CSV file...'
      case 'completed':
        return 'Export completed!'
      case 'error':
        return error?.message || 'Export failed'
      case 'cancelled':
        return 'Export cancelled'
      default:
        return 'Processing...'
    }
  }

  const statusMessage = getStatusMessage()

  if (isMinimized) {
    return (
      <div
        className={cn(
          'flex items-center gap-3 px-4 py-3 w-full h-full',
          'transition-all duration-300 ease-in-out',
          containerClass,
          isAnimating && 'scale-95',
          className,
        )}
      >
        {/* Status Icon */}
        <i className={cn('text-sm', iconClass)} />

        {/* Minimized Content */}
        <div className='flex-1 min-w-0'>
          <p className='text-sm font-medium text-neutral-primary truncate'>{config.filename}</p>
          {isActive && (
            <p className='text-xs text-neutral-secondary'>
              {currentRecords !== undefined && totalRecords !== undefined
                ? `${currentRecords}/${totalRecords} records (${progressPercentage}%)`
                : `${progressPercentage}% complete`}
            </p>
          )}
        </div>

        {/* Progress Ring for Active Exports */}
        {isActive && (
          <div className='relative w-6 h-6'>
            <svg
              className='w-6 h-6 transform -rotate-90'
              viewBox='0 0 24 24'
            >
              <circle
                cx='12'
                cy='12'
                r='10'
                stroke='currentColor'
                strokeWidth='2'
                fill='none'
                className='text-neutral-20'
              />
              <circle
                cx='12'
                cy='12'
                r='10'
                stroke='currentColor'
                strokeWidth='2'
                fill='none'
                strokeDasharray={`${2 * Math.PI * 10}`}
                strokeDashoffset={`${2 * Math.PI * 10 * (1 - percentage / 100)}`}
                className={cn('transition-all duration-300', progressBarClass.replace('bg-', 'text-'))}
                strokeLinecap='round'
              />
            </svg>
          </div>
        )}

        {/* Expand Button */}
        <ButtonIcon
          icon='fa-solid fa-chevron-up'
          size='small'
          kind={ButtonIconKind.primary}
          onClick={handleExpand}
          className='ml-2'
        />
      </div>
    )
  }

  return (
    <div
      className={cn(
        'w-full',
        'transition-all duration-300 ease-in-out',
        containerClass,
        isAnimating && 'scale-95',
        className,
      )}
    >
      {/* Header */}
      <div className='flex items-center justify-between p-4 pb-2'>
        <div className='flex items-center gap-2'>
          <i className={cn('text-sm', iconClass)} />
          <span className='text-sm font-medium text-neutral-primary'>CSV Export</span>
        </div>

        <div className='flex items-center gap-1'>
          {/* Minimize Button */}
          <ButtonIcon
            icon='fa-solid fa-chevron-down'
            size='small'
            kind={ButtonIconKind.primary}
            onClick={handleMinimize}
          />

          {/* Cancel/Close Button */}
          <ButtonIcon
            icon='fa-solid fa-xmark'
            size='small'
            kind={ButtonIconKind.primary}
            onClick={handleClose}
            aria-label={isActive ? 'Cancel export' : 'Close notification'}
          />
        </div>
      </div>

      {/* Content */}
      <div className='px-4 pb-4'>
        {/* File Name */}
        <h3 className='text-sm font-medium text-neutral-primary mb-1 truncate'>{config.filename}</h3>

        {/* Status Message */}
        <p className='text-xs text-neutral-secondary mb-3'>{statusMessage}</p>

        {/* Progress Bar (for active exports) */}
        {isActive && (
          <div className='mb-3'>
            <div className='flex justify-between items-center mb-1'>
              <span className='text-xs text-neutral-secondary'>
                {currentRecords !== undefined && totalRecords !== undefined
                  ? `${currentRecords}/${totalRecords} records`
                  : 'Preparing...'}
              </span>
              <span className='text-xs font-medium text-neutral-primary'>{progressPercentage}%</span>
            </div>

            <div className='w-full bg-neutral-20 rounded-full h-1.5'>
              <div
                className={cn('h-1.5 rounded-full transition-all duration-500 ease-out', progressBarClass)}
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
        )}

        {/* Error Message */}
        {isError && error && (
          <div className='mb-3 p-2 bg-status-critical-light rounded border border-status-critical-10'>
            <p className='text-xs text-status-critical'>{error.message}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className='flex gap-2'>
          {isCompleted && (
            <button
              onClick={handleDownload}
              className='flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-brand-primary-rest text-white text-sm font-medium rounded-lg hover:bg-brand-primary-dark transition-colors'
            >
              <i className='fa-solid fa-download text-xs' />
              Download CSV
            </button>
          )}

          {(isError || isCompleted) && (
            <ButtonIcon
              icon='fa-solid fa-trash'
              size='small'
              kind={ButtonIconKind.primary}
              onClick={handleCancel}
              className='text-neutral-secondary hover:text-status-critical'
            />
          )}
        </div>
      </div>
    </div>
  )
}
